import { getEnv } from '@/common/api';
import React, { PropsWithChildren, createContext, useCallback, useRef } from 'react';
import { KFlatList } from '.';

export const FlatListContext = createContext({ visibleListeners: [], listeners: [], initContext: false });

/**
 * 增加了埋点功能，和ViewVisible埋点组件互动
 */
export function flatListWrapper<T extends object>(WrappedComponent) {
    return React.forwardRef((props: T & PropsWithChildren<any>, ref) => {
        const state = useRef({
            // 判断是否提供了Context
            initContext: true,
            // 曝光监听的listener
            listeners: [],
            // 曝光过的callback
            visibleListeners: [],
        });

        const onViewableItemsChanged = useCallback(item => {
            props.onViewableItemsChanged?.(item);
            item.viewableItems
                ?.filter(item => item.isViewable)
                ?.forEach(item => {
                    // 执行曝光事件
                    state.current.listeners.forEach(({ itemKey, listener }) => {
                        if (!props.keyExtractor) {
                            if (getEnv() !== 'prod') throw new Error('FlatList必须指定keyExtractor');
                        }
                        if (
                            // key相同
                            itemKey === item.key &&
                            // 且没有曝光过
                            !state.current.visibleListeners.includes(listener)
                        ) {
                            // 已经曝光过的记录一下
                            state.current.visibleListeners.push(listener);
                            // 触发曝光
                            listener();
                        }
                    });
                });
        }, []);

        const flatProps = {
            onViewableItemsChanged,
            viewabilityConfig: {
                minimumViewTime: 300,
                waitForInteraction: false,
                viewAreaCoveragePercentThreshold: 60,
            },
        };

        return (
            <FlatListContext.Provider value={state.current}>
                <WrappedComponent ref={ref} {...props} {...flatProps} />
            </FlatListContext.Provider>
        );
    });
}

export const FlatList = flatListWrapper(KFlatList) as typeof KFlatList;
